import type { QueryKey } from "@tanstack/react-query";
import { processesKey } from "@/queries/processes";
import { scopedKey, type QueryScope } from "@/queries/scope";
import { observedRead, type THintTarget, type THintTargets } from "./types";

/** One row over several key prefixes, each marked and re-read as `observedRead` does. */
function observedReads(...keysOf: ReadonlyArray<(scope: QueryScope) => QueryKey>): THintTarget {
  const rows = keysOf.map((keyOf) => observedRead(keyOf));
  return {
    mark: (root, slug, cached) => rows.forEach((row) => row.mark(root, slug, cached)),
    reread: (root, slug, cached) => rows.forEach((row) => row.reread(root, slug, cached)),
  };
}

/**
 * The processes and templates domain's hint rows (NG-2799).
 */
export const PROCESSES_TEMPLATES_HINTS: THintTargets = {
  // The process bank, the pickable list and each process's usage, all under
  // `'processes'`. Another admin's process or stage write marks them, and an
  // open picker, stage reader or editor re-reads.
  "processes:bank": observedRead(processesKey),
  // The pickable templates (`'project-templates'`) and every open template's
  // detail and work-item attachments (`'project-template'`). Each template tab
  // hydrates its form once, so a re-read never replaces an edit in progress.
  "project-templates:bank": observedReads(
    (scope) => scopedKey(scope, "project-templates"),
    (scope) => scopedKey(scope, "project-template"),
  ),
  // The workspace template list (`'workspace-templates'`) and each template's
  // versions (`'workspace-template'`).
  "workspace-templates:bank": observedReads(
    (scope) => scopedKey(scope, "workspace-templates"),
    (scope) => scopedKey(scope, "workspace-template"),
  ),
};
